import React from 'react';
import { motion } from 'framer-motion';
import { Check } from 'lucide-react';
import whyUsLesson from '@/assets/why_us/why_us_lesson.png';
import whyUsTime from '@/assets/why_us/why_us_time.png';
import whyUsVerify from '@/assets/why_us/why_us_verify.png';
import { useI18n } from '@/i18n';

const featureRows = [
  {
    title: 'Verified & Reliable Tutors',
    description: 'AI + human checks, done right.',
    bullets: [
      'Education background checked.',
      'Identity verified.',
      'Only trusted tutors make the cut.',
    ],
    imageSrc: whyUsVerify,
    imageAlt: 'Verified tutor illustration',
  },
  {
    title: 'Personalized Learning',
    description: 'Lessons shaped around each student.',
    bullets: [
      '48+ subjects across Malaysian curricula.',
      'Includes SPM, IGCSE, A-Level, UEC, and more.',
      'Tutors adapt pace and style for each student.',
    ],
    imageSrc: whyUsLesson,
    imageAlt: 'Personalized learning illustration',
  },
  {
    title: 'Affordable & Flexible',
    description: 'Good help that fits real life.',
    bullets: [
      'Set lesson times around your own schedule.',
      'Clear pricing, shown upfront.',
      'No hidden fees. No surprise charges.',
      'Great tutoring without the usual hassle.',
    ],
    imageSrc: whyUsTime,
    imageAlt: 'Flexible tutoring illustration',
  },
];

const musicFeatureRows = [
  {
    title: 'Verified & Reliable Music Tutors',
    description: 'Confidence starts with the right teacher.',
    bullets: [
      'Teaching experience and background reviewed.',
      'Identity and supporting documents checked.',
      'Profiles built around real musical expertise.',
    ],
    imageSrc: whyUsVerify,
    imageAlt: 'Verified music tutor illustration',
  },
  {
    title: 'Lessons Built Around You',
    description: 'Learn at your own pace and in your own style.',
    bullets: [
      'Choose your instrument, level, and musical goals.',
      'Suitable for beginners, exam preparation, and hobbyists.',
      'Tutors adapt each lesson to the learner.',
    ],
    imageSrc: whyUsLesson,
    imageAlt: 'Personalized music lesson illustration',
  },
  {
    title: 'Flexible & Accessible',
    description: 'Music lessons that fit real life.',
    bullets: [
      'Arrange lessons around your weekly schedule.',
      'Choose online, in-person, or flexible learning.',
      'See clear lesson rates before getting started.',
    ],
    imageSrc: whyUsTime,
    imageAlt: 'Flexible music tutoring illustration',
  },
];

const sectionIntroVariants = {
  hidden: { opacity: 0, y: 18 },
  visible: {
    opacity: 1,
    y: 0,
    transition: { duration: 0.55 },
  },
};

const rowVariants = {
  hidden: { opacity: 0, y: 24 },
  visible: {
    opacity: 1,
    y: 0,
    transition: { duration: 0.55 },
  },
};

interface FeaturesSectionProps {
  variant?: 'academic' | 'music';
}

const FeaturesSection: React.FC<FeaturesSectionProps> = ({ variant = 'academic' }) => {
  const { locale } = useI18n();
  const isZh = locale === 'zh-CN';
  const academicRows = isZh
    ? [
        { title: '认证可靠导师', description: 'AI 加人工审核，做得更稳妥。', bullets: ['学历背景已核对。', '身份资料已验证。', '只有值得信赖的导师才能上线。'], imageSrc: whyUsVerify, imageAlt: '导师认证插图' },
        { title: '更贴合学生的学习方式', description: '课程围绕每位学生而设计。', bullets: ['覆盖马来西亚课程体系的 48+ 科目。', '包含 SPM、IGCSE、A-Level、UEC 等。', '导师会按学生节奏与风格调整教学。'], imageSrc: whyUsLesson, imageAlt: '个性化学习插图' },
        { title: '价格透明又灵活', description: '适合真实生活安排的好补习。', bullets: ['上课时间可按你的行程安排。', '收费清楚透明。', '没有隐藏费用，也没有临时加价。', '更省心地获得优质补习。'], imageSrc: whyUsTime, imageAlt: '灵活补习插图' },
      ]
    : featureRows;
  const rows = variant === 'music' ? musicFeatureRows : academicRows;
  return (
    <section className="bg-white py-14 md:py-20">
      <div className="container mx-auto max-w-6xl px-4">
        <motion.div
          initial="hidden"
          whileInView="visible"
          viewport={{ once: true, amount: 0.4 }}
          variants={sectionIntroVariants}
          className="mx-auto mb-12 max-w-2xl text-center md:mb-16"
        >
          <h2 className={`text-4xl font-bold leading-tight tracking-tight md:text-5xl ${variant === 'music' ? 'text-gray-900' : 'text-primary'}`}>
            {variant === 'music' ? 'Why Tutor Founder Music' : isZh ? '为什么选择 Tutor Founder' : 'Why Tutor Founder'}
          </h2>
        </motion.div>

        <div className="space-y-10 md:space-y-14">
          {rows.map(({ title, description, bullets, imageSrc, imageAlt }, index) => (
            <motion.div
              key={title}
              initial="hidden"
              whileInView="visible"
              viewport={{ once: true, amount: 0.25 }}
              variants={rowVariants}
              className={`grid items-center gap-6 md:gap-10 lg:grid-cols-2 ${
                index % 2 === 1 ? 'lg:[&>*:first-child]:order-2 lg:[&>*:last-child]:order-1' : ''
              }`}
            >
              <div className="overflow-hidden rounded-[1.5rem]">
                <img
                  src={imageSrc}
                  alt={imageAlt}
                  className="aspect-[4/3] w-full object-cover"
                />
              </div>

              <div className="max-w-xl px-4 md:px-0">
                <h3 className="text-2xl font-semibold leading-tight text-gray-900 md:text-3xl">
                  {title}
                </h3>
                <p className="mt-1 text-base leading-8 text-gray-600 md:text-lg">
                  {description}
                </p>
                <div className="mt-6 space-y-3">
                  {bullets.map((bullet) => (
                    <div key={bullet} className="flex items-start gap-3">
                      <div className={`mt-0.5 inline-flex h-6 w-6 shrink-0 items-center justify-center rounded-full text-primary ${variant === 'music' ? 'bg-primary-50' : 'bg-primary/10'}`}>
                        <Check className="h-4 w-4" strokeWidth={3} />
                      </div>
                      <p className="text-sm leading-7 text-gray-900 md:text-base">{bullet}</p>
                    </div>
                  ))}
                </div>
              </div>
            </motion.div>
          ))}
        </div>
      </div>
    </section>
  );
};

export default FeaturesSection;
